import { useDispatch, useSelector } from "react-redux";
import { ArrowButton, Button, Input } from "@common/index";
import { ftInToFt, ftToIn, kgToLbs } from "@utils/measurements";
import { BMI } from "./BMI";
import styles from "./styles.module.scss";
import { useEffect, useState } from "react";
import { UserService } from "@api/services/userService";
import { getUserInfoThunk } from "src/redux/slices/userSlice";
import { MeasurementsSwitch } from "src/routes/onboarding/shared/components";
import { useMeasureSystem } from "@hooks/useMeasureSystem";

const Measures = {
  us: "lbs",
  eu: "kg",
};

export const Weight = ({ onNextStep, onTrackEvent }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userInfo);
  const { ms, changeMs } = useMeasureSystem();

  const [weight, setWeight] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [bmi, setBmi] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("userWeight");
    if (saved) {
      try {
        const { kg, lbs } = JSON.parse(saved);
        const value = ms === "us" ? lbs : kg;
        setWeight(
          value === undefined || value === null || Number.isNaN(value) ? "" : String(value)
        );
        validateWeight(value, ms);
      } catch {
      }
    }
  }, [ms]);

  const validateWeight = (val, system) => {
    if (val === "" || val === undefined || val === null) {
      setIsValid(false);
      return;
    }
    const numericValue = Number(val);
    if (Number.isNaN(numericValue)) {
      setIsValid(false);
      return;
    }
    if (system === "us") {
      setIsValid(numericValue >= 55 && numericValue <= 662);
    } else {
      setIsValid(numericValue >= 25 && numericValue <= 300);
    }
  };

  
  const handleChange = (e) => {
    const val = e.currentTarget.value;

    if (val === "") {
      setWeight("");
      setIsValid(false);
     
      const cleared = ms === "us"
        ? { lbs: undefined, kg: undefined }
        : { kg: undefined, lbs: undefined };
      localStorage.setItem("userWeight", JSON.stringify(cleared));
      return;
    }


    if (isNaN(val)) return;

    setWeight(val);
    validateWeight(val, ms);

 
    const num = Number(val);
    const kg = ms === "us" ? Math.round(num / 2.20462) : num;
    const lbs = ms === "us" ? num : Math.round(num * 2.20462);
    localStorage.setItem("userWeight", JSON.stringify({ kg, lbs }));
  };

  
  const handleNextStep = async () => {
    try {
      setLoading(true);

      const current = Number(weight);
      const weightKg = ms === "us" ? Math.round(current / 2.20462) : current;
      const weightLbs = ms === "us" ? current : Math.round(current * 2.20462);

     
      localStorage.setItem(
        "userWeight",
        JSON.stringify({ kg: weightKg, lbs: weightLbs })
      );

      await UserService.updateQuestions({
        qqWeightKg: weightKg,
        qqWeightLbs: weightLbs,
      });

      onNextStep();

      const weightValue = ms === "us" ? `${weightLbs} lbs` : `${weightKg} kg`;
      onTrackEvent?.(weightValue);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const height =
      ms === "us"
        ? ftToIn(
            ftInToFt(user?.questions?.qqHeightFt, user?.questions?.qqHeightIn)
          )
        : +user?.questions?.qqHeightCm / 100;

    if (!weight || !height) return;

    const numeric = Number(weight);
    if (Number.isNaN(numeric)) return;

    const bmiCalculated = numeric / (height * height);
    const bmiToState = Math.round(ms === "us" ? bmiCalculated * 703 : bmiCalculated);
    setBmi(bmiToState);
  }, [weight, ms, user]);

  useEffect(() => {
    dispatch(getUserInfoThunk());
  }, [dispatch]);

 
  const handleMeasurementChange = (nextMs) => {
    if (nextMs === ms) return;

    if (weight) {
      const numeric = Number(weight);
      if (!Number.isNaN(numeric)) {
        const converted =
          nextMs === "us" ? Math.round(numeric * 2.20462) : Math.round(numeric / 2.20462);
        setWeight(String(converted));
        validateWeight(converted, nextMs);

        const kg = nextMs === "us" ? Math.round(converted / 2.20462) : converted;
        const lbs = nextMs === "us" ? converted : Math.round(converted * 2.20462);
        localStorage.setItem("userWeight", JSON.stringify({ kg, lbs }));
      } else {
        setWeight("");
        setIsValid(false);
      }
    } else {
      setWeight("");
      setIsValid(false);
      localStorage.removeItem("userWeight");
    }
    changeMs(nextMs);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2>What's your current weight?</h2>
        <MeasurementsSwitch
          ms={ms}
          changeMs={handleMeasurementChange}
          units={["lbs", "kg"]}
        />
        <div className={styles.inputWrapper}>
          <div className={styles.inputContainer}>
            <Input
              inputmode="numeric"
              type="number"
              step="1"
              placeholder={"Your weight"}
              value={weight}
              onChange={handleChange}
              endAdornment={<h5>{Measures[ms]}</h5>}
            />
            <ArrowButton
              variant="walky"
              isValid={isValid}
              handleNextStep={handleNextStep}
            />
          </div>
          <p className={styles.hint}>
            Please, enter a value from{" "}
            <span>
              {ms === "eu"
                ? `25 kg to 300 kg`
                : `${kgToLbs(25).toFixed(0)} lbs to ${kgToLbs(300).toFixed(0)} lbs`}
            </span>
          </p>
        </div>
        {isValid && <BMI bmi={bmi} />}
      </div>
      <div className={styles.action}>
        <Button
          variant="walky"
          onClick={handleNextStep}
          disabled={!isValid || loading}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
